import React, { useState } from 'react';
import { Accordion, Button, Card, useAccordionButton } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { PolicyCommissionInvoiceList } from '@models/PolicyCommissionInvoice';

import { selectFeaturePolicyCommissionInvoice } from './selector';
import {
  policyCommissionInvoiceCreate,
  PolicyCommissionInvoiceCreatePayload,
  policyCommissionInvoiceFetch,
  PolicyCommissionInvoiceFetchPayload,
  stateUpdate,
  StateUpdatePayload,
} from './action';
import { PolicyCommissionInvoiceListView } from './policy-commission-invoice-list-view';
import { PolicyCommissionInvoiceFormModal, PolicyCommissionInvoiceFormValues } from './policy-commission-invoice-form-modal';
import { PolicyCommissionInvoiceDetailsModal } from './policy-commission-invoice-details-modal';

const Toggle = ({ children, eventKey }: { children: React.ReactNode; eventKey: string }) => {
  const handleClick = useAccordionButton(eventKey);

  return (
    <Card.Link className="c-pointer link-info" onClick={handleClick}>
      {children}
    </Card.Link>
  );
};

type Props = { id: number; canCreate?: boolean };

const PolicyCommissionInvoiceCardBody = ({
  id: insurancePolicyId,
  canCreate = false,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { pagination } = useSelector(selectFeaturePolicyCommissionInvoice);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const handleShow = (invoice: PolicyCommissionInvoiceList) => {
    const payload: PolicyCommissionInvoiceFetchPayload = { id: invoice.id };
    dispatch(policyCommissionInvoiceFetch(payload));
    setShowDetailsModal(true);
  }

  return (
    <>
      <Card.Body>
        <Accordion flush>
          <Card.Title className="d-flex flex-row justify-content-between align-items-center">
            <Toggle eventKey="0">
              Commission Invoices ({pagination?.total ?? 0})
            </Toggle>
            {canCreate && (
              <Button onClick={() => setShowFormModal(true)} variant="outline-primary">
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                {t('common:add')}
              </Button>
            )}
          </Card.Title>
          <Accordion.Collapse eventKey="0">
            <PolicyCommissionInvoiceListView id={insurancePolicyId} onShow={handleShow} />
          </Accordion.Collapse>
        </Accordion>
      </Card.Body>

      <PolicyCommissionInvoiceFormModal
        show={showFormModal}
        onHide={() => setShowFormModal(false)}
        onSubmit={(values: PolicyCommissionInvoiceFormValues) => {
          const payload: PolicyCommissionInvoiceCreatePayload = { insurancePolicyId, ...values };
          dispatch(policyCommissionInvoiceCreate(payload));
          setShowFormModal(false);
        }}
      />

      <PolicyCommissionInvoiceDetailsModal
        show={showDetailsModal}
        onHide={() => {
          setShowDetailsModal(false);
          const payload: StateUpdatePayload = { invoice: undefined };
          dispatch(stateUpdate(payload));
        }}
      />
    </>
  );
};

export { PolicyCommissionInvoiceCardBody };
